import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { datumOmgedraaid, type Datumbereik, type Kolom } from '@xxtract/tabelfilters';
import { CONTROL_CLASS, FOCUS_CLASS } from './stijl';

const DATUM_FIELDS = [
  { rol: 'van', labelKey: 'tabel.vanaf' },
  { rol: 'tot', labelKey: 'tabel.totEnMet' },
] as const;

interface DatumFilterProps {
  /** Alleen de beschrijving van de kolom; `never` laat elke rijvorm toe. */
  kolom: Kolom<never, never>;
  bereik?: Datumbereik;
  onChange: (bereik: Datumbereik) => void;
  /** Paginafilter (story 12): één kader met rand, elk label op de regel van zijn eigen veld. */
  compact?: boolean;
  testId?: string;
}

/** Twee native datumvelden "Vanaf" en "Tot en met" in één groep (CAP-3, §3). */
export function DatumFilter({ kolom, bereik = {}, onChange, compact, testId }: DatumFilterProps) {
  const { t } = useTranslation();
  const id = useId();
  // Eigen kopie van de velden: een half ingevuld datumveld blijft staan tot de stand terugmeldt.
  const [lokaal, setLokaal] = useState(bereik);
  const [vorig, setVorig] = useState(bereik);
  if (vorig.van !== bereik.van || vorig.tot !== bereik.tot) {
    setVorig(bereik);
    setLokaal(bereik);
  }
  const omgedraaid = datumOmgedraaid(lokaal);
  const meldingId = `${id}-melding`;

  const zet = (rol: keyof Datumbereik, waarde: string) => {
    const { [rol]: _oud, ...rest } = lokaal;
    const nieuw = waarde ? { ...rest, [rol]: waarde } : rest;
    setLokaal(nieuw);
    onChange(nieuw);
  };

  const veld = (rol: keyof Datumbereik, labelKey: string) => (
    // Label en veld lopen samen terug, nooit los van elkaar (spec-filterbalk-smal-scherm CAP-1).
    <div className={compact ? 'flex items-center gap-1' : 'flex w-full flex-col gap-1 md:w-auto'} key={rol}>
      <label className={`${compact ? 'whitespace-nowrap ' : ''}text-xs text-[var(--foreground-muted)]`} htmlFor={`${id}-${rol}`}>{t(labelKey)}</label>
      <input
        aria-describedby={omgedraaid ? meldingId : undefined}
        aria-invalid={omgedraaid || undefined}
        className={compact ? `min-h-11 md:min-h-9 rounded-[var(--radius-control)] border-0 bg-transparent px-1 text-sm text-[var(--foreground)] ${FOCUS_CLASS}` : `${CONTROL_CLASS} w-full md:w-auto`}
        id={`${id}-${rol}`}
        onChange={(e) => {
          // Een half getypte datum meldt '' met badInput: niet wissen midden in het typen.
          if (e.target.value === '' && e.target.validity.badInput) return;
          zet(rol, e.target.value);
        }}
        type="date"
        value={lokaal[rol] ?? ''}
      />
    </div>
  );

  if (compact) {
    return (
      <fieldset className="min-w-0">
        <legend className="sr-only">{t(kolom.kopSleutel)}</legend>
        <div className="flex flex-wrap items-center gap-x-2 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-2" data-testid={testId}>
          {DATUM_FIELDS.map(({ rol, labelKey }) => veld(rol, labelKey))}
        </div>
        {omgedraaid ? (
          <p className="mt-1 text-xs text-[var(--destructive)]" id={meldingId}>{t('tabel.datumOmgedraaid')}</p>
        ) : null}
      </fieldset>
    );
  }

  return (
    <fieldset className="flex w-full min-w-0 flex-col gap-1 md:w-auto">
      <legend className="mb-1 text-xs font-medium text-[var(--foreground-muted)]">{t(kolom.kopSleutel)}</legend>
      <div className="flex flex-wrap gap-2">
        {DATUM_FIELDS.map(({ rol, labelKey }) => veld(rol, labelKey))}
      </div>
      {omgedraaid ? (
        <p className="text-xs text-[var(--destructive)]" id={meldingId}>{t('tabel.datumOmgedraaid')}</p>
      ) : null}
    </fieldset>
  );
}
